import {
  FileText,
  Database,
  Shield,
  Cloud,
  Clock,
  Key,
  Cookie,
  Baby,
  Globe,
  RefreshCw,
  Mail,
} from 'lucide-react';
import { LegalPageLayout } from '../components/legal/LegalPageLayout';

const sections = [
  { id: 'introduction', label: 'Introduction', icon: <FileText className="w-4 h-4" /> },
  { id: 'collect', label: 'What We Collect', icon: <Database className="w-4 h-4" /> },
  { id: 'use', label: 'How We Use Data', icon: <Shield className="w-4 h-4" /> },
  { id: 'third-party', label: 'Infrastructure Providers', icon: <Cloud className="w-4 h-4" /> },
  { id: 'storage', label: 'Storage & Security', icon: <Database className="w-4 h-4" /> },
  { id: 'retention', label: 'Data Retention', icon: <Clock className="w-4 h-4" /> },
  { id: 'rights', label: 'Your Rights', icon: <Key className="w-4 h-4" /> },
  { id: 'cookies', label: 'Cookies & Tracking', icon: <Cookie className="w-4 h-4" /> },
  { id: 'children', label: "Children's Privacy", icon: <Baby className="w-4 h-4" /> },
  { id: 'international', label: 'GDPR & CCPA', icon: <Globe className="w-4 h-4" /> },
  { id: 'changes', label: 'Policy Changes', icon: <RefreshCw className="w-4 h-4" /> },
  { id: 'contact', label: 'Contact', icon: <Mail className="w-4 h-4" /> },
];

const h2 = 'text-2xl font-semibold text-foreground mt-10';
const h3 = 'text-lg font-semibold text-foreground mt-4';
const body = 'text-base leading-relaxed text-foreground/80';
const list = 'list-disc space-y-2 pl-6 text-foreground/80';
const link = 'text-primary hover:underline';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="Last Updated: May 20, 2026"
      sections={sections}
    >
      <div className="space-y-6">
        <section id="introduction" className="space-y-4 scroll-mt-8">
          <p className={body}>
            This Privacy Policy explains what personal information Polymyth collects when you use
            the platform at{' '}
            <a href="https://polymyth.quest" className={link}>
              polymyth.quest
            </a>{' '}
            and the Polymyth mobile applications for iOS and Android (the "Mobile Apps"), how we use
            it, who we share it with, and the choices you have. It applies to the Polymyth web
            application, the Mobile Apps, and related services (together, the "Service").
          </p>
          <div className="rounded-lg border border-accent-gold/40 bg-accent-gold/5 p-4">
            <p className={body}>
              <strong>Beta notice.</strong> Polymyth is in beta. During beta, we may use the content
              you create in the Service — characters, sessions, narratives, journals, and related
              telemetry — to operate, debug, evaluate, and improve the Service, including internal
              review by Polymyth staff. We will revisit this practice for general availability and
              update this policy accordingly.
            </p>
          </div>
        </section>

        <section id="collect" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>1. What We Collect</h2>

          <h3 className={h3}>Account Information</h3>
          <ul className={list}>
            <li>Email address — required for authentication and account recovery.</li>
            <li>Display name — chosen by you, shown in-game and in lobby.</li>
            <li>Password hash (if you use password login) — we never store plaintext passwords.</li>
            <li>
              Passkey credential metadata (WebAuthn) — public key, credential ID, device type, last
              used timestamp and counter. We do not see or store biometrics.
            </li>
            <li>Email verification status.</li>
          </ul>

          <h3 className={h3}>Gameplay Data</h3>
          <ul className={list}>
            <li>
              Characters you create: name, class, species, background, stats, equipment, biography.
            </li>
            <li>
              Session content: scenes, round narratives, combat logs, NPC dialogue, journal entries,
              quest state, mechanical state (HP, conditions, inventory, etc.).
            </li>
            <li>Your in-game text input and choices ("Inputs").</li>
            <li>AI-generated narrative and system outputs produced during play.</li>
            <li>Token balance, token usage history, and in-app action history.</li>
          </ul>

          <h3 className={h3}>Mobile App Data</h3>
          <ul className={list}>
            <li>
              Device identifiers such as Apple's IDFV (Identifier for Vendor), Android Advertising
              ID (only when present; we do not use it for advertising), and a Firebase Installation
              ID. We do not request Apple's IDFA and do not display the App Tracking Transparency
              prompt.
            </li>
            <li>
              Push notification tokens (APNs on iOS, FCM on Android) — only if you grant
              notification permission. Used to send gameplay and account notifications.
            </li>
            <li>
              In-app purchase receipts and transaction identifiers from Apple App Store and Google
              Play, used to validate purchases and credit your token balance. We do not see or store
              your payment card, bank, or store-account credentials — those stay with Apple or
              Google.
            </li>
            <li>
              App version, build number, locale, time zone, and device model — sent with requests
              and crash reports to help us debug.
            </li>
          </ul>

          <h3 className={h3}>Technical Metadata</h3>
          <ul className={list}>
            <li>IP address, user agent, device type, browser or app version, operating system.</li>
            <li>Session timestamps, connection events, WebSocket diagnostics.</li>
            <li>
              Error logs, stack traces, performance traces, and crash reports (via Firebase
              Crashlytics on the Mobile Apps) used to diagnose and improve the Service.
            </li>
          </ul>

          <h3 className={h3}>Communications</h3>
          <ul className={list}>
            <li>Support messages you send us and our replies.</li>
            <li>Optional feedback and bug reports.</li>
          </ul>
        </section>

        <section id="use" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>2. How We Use Your Information</h2>
          <ul className={list}>
            <li>
              <strong>Provide the Service:</strong> authenticate you, deliver game sessions,
              generate AI narratives, resolve mechanics, persist your characters and progress.
            </li>
            <li>
              <strong>Account security:</strong> detect and prevent abuse, unauthorized access,
              fraud, and exploits.
            </li>
            <li>
              <strong>Improve the product (beta):</strong> review gameplay content and logs to
              diagnose bugs, tune prompts, train evaluation benchmarks, and improve the game engine.
              We do not sell this data.
            </li>
            <li>
              <strong>Essential communications:</strong> send transactional emails (account
              verification, password reset, security notices, service disruption notices).
            </li>
            <li>
              <strong>Legal compliance:</strong> respond to lawful requests, enforce our Terms of
              Service, comply with applicable law.
            </li>
          </ul>
          <p className={body}>
            <strong>We do not share or sell your personal information to third parties.</strong> We
            do not use your data for third-party advertising, and we do not provide it to data
            brokers, ad networks, or analytics partners.
          </p>
        </section>

        <section id="third-party" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>3. Infrastructure Providers</h2>
          <p className={body}>
            Polymyth runs on infrastructure operated by a small number of providers that process
            data <em>on our behalf</em> strictly to deliver the Service. These providers act as our
            processors under contract — they may not use your data for their own purposes, do not
            sell it, and do not combine it with data from other sources for advertising.
          </p>

          <h3 className={h3}>Google Cloud Platform / Firebase</h3>
          <ul className={list}>
            <li>
              Role: authentication, Firestore database, Cloud Run hosting, file storage, push
              notification delivery (FCM), crash reporting (Crashlytics) on the Mobile Apps.
            </li>
            <li>
              Terms:{' '}
              <a
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                firebase.google.com/support/privacy
              </a>
            </li>
          </ul>

          <h3 className={h3}>Google Gemini (AI Provider)</h3>
          <ul className={list}>
            <li>
              Role: generate narrative, dialogue, NPC reasoning, combat resolution, and other game
              content during play. Receives your Inputs and the relevant game state needed to
              produce the response.
            </li>
            <li>
              Polymyth uses Google's enterprise Gemini API; prompts and responses are processed
              under Google's API terms and are not used by Google to train generally available
              models.
            </li>
            <li>
              Terms:{' '}
              <a
                href="https://ai.google.dev/gemini-api/terms"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                ai.google.dev/gemini-api/terms
              </a>
            </li>
          </ul>

          <h3 className={h3}>Apple App Store & Google Play (Mobile Apps)</h3>
          <ul className={list}>
            <li>
              Role: app distribution and in-app purchase processing. Apple and Google receive
              purchase and device information directly from your device under their own terms; they
              pass us only an opaque transaction identifier and receipt so we can credit your token
              balance. We never see your store password or payment instrument.
            </li>
            <li>
              Terms:{' '}
              <a
                href="https://www.apple.com/legal/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                apple.com/legal/privacy
              </a>{' '}
              ·{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                policies.google.com/privacy
              </a>
            </li>
          </ul>

          <h3 className={h3}>Cloudflare</h3>
          <ul className={list}>
            <li>
              Role: DNS, CDN, DDoS protection, and traffic routing for polymyth.quest. Sees request
              metadata (IP, user agent, path) at the network layer in order to serve the site; does
              not receive your account data or gameplay content.
            </li>
            <li>
              Terms:{' '}
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                cloudflare.com/privacypolicy
              </a>
            </li>
          </ul>
        </section>

        <section id="storage" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>4. Storage & Security</h2>
          <ul className={list}>
            <li>Data is stored in Google Cloud data centers, primarily in the United States.</li>
            <li>Data is encrypted in transit (TLS/HTTPS) and at rest.</li>
            <li>
              Access to production data is limited to Polymyth personnel and contractors who need it
              to operate, debug, or improve the Service, and is logged.
            </li>
            <li>
              We apply reasonable administrative, technical, and physical safeguards; no system is
              ever fully secure. Report suspected incidents to{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
              .
            </li>
          </ul>
        </section>

        <section id="retention" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>5. Data Retention</h2>
          <ul className={list}>
            <li>
              <strong>Active accounts:</strong> we retain your account data and gameplay records as
              long as your account is active.
            </li>
            <li>
              <strong>Deleted accounts:</strong> when you request deletion, personal data is
              scheduled for removal and deleted from active systems within 30 days.
            </li>
            <li>
              <strong>Backups:</strong> residual copies may persist in encrypted backups for up to
              90 days before being overwritten.
            </li>
            <li>
              <strong>Logs and aggregated analytics:</strong> we may retain de-identified or
              aggregated data (with no personal identifiers) indefinitely for product improvement.
            </li>
            <li>
              <strong>Beta data wipes:</strong> as noted in the Terms, all beta data may be reset at
              any time during beta.
            </li>
          </ul>
        </section>

        <section id="rights" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>6. Your Rights</h2>
          <p className={body}>You can:</p>
          <ul className={list}>
            <li>
              <strong>Access:</strong> request a copy of personal data we hold about you.
            </li>
            <li>
              <strong>Correct:</strong> update your display name, email, and profile information in
              settings.
            </li>
            <li>
              <strong>Delete:</strong> request account deletion in settings or by emailing us. We
              will delete personal data subject to legal retention obligations.
            </li>
            <li>
              <strong>Export:</strong> request a machine-readable export of your account data.
            </li>
            <li>
              <strong>Object / restrict:</strong> ask us to stop or limit specific processing.
            </li>
          </ul>
          <p className={body}>
            To exercise these rights, email{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>{' '}
            from the address associated with your account. We may ask you to verify your identity.
          </p>
        </section>

        <section id="cookies" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>7. Cookies & Tracking</h2>
          <p className={body}>We use minimal storage:</p>
          <ul className={list}>
            <li>
              <strong>Essential cookies / tokens:</strong> authentication session, passkey state.
              Required for the Service to work.
            </li>
            <li>
              <strong>Local browser storage:</strong> UI preferences, volume settings, last-seen
              state.
            </li>
          </ul>
          <p className={body}>
            We do not use advertising cookies, cross-site tracking pixels, or third-party behavioral
            analytics on the beta.
          </p>
          <p className={body}>
            <strong>Mobile Apps:</strong> the iOS and Android apps use the device's secure local
            storage for authentication tokens and UI preferences instead of browser cookies. We do
            not use Apple's IDFA and do not display the App Tracking Transparency prompt. You can
            revoke push-notification permission and reset Firebase Crashlytics opt-in at any time
            from your device settings.
          </p>
        </section>

        <section id="children" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>8. Children's Privacy</h2>
          <p className={body}>
            Polymyth is not directed to children under 13, and we do not knowingly collect personal
            information from children under 13. If you believe a child under 13 has created an
            account or provided us with personal data, contact{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>{' '}
            and we will delete the account and data.
          </p>
        </section>

        <section id="international" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>9. International Users (GDPR & CCPA)</h2>
          <p className={body}>
            If you are in the European Economic Area, United Kingdom, or Switzerland, you have
            rights under the General Data Protection Regulation (GDPR) including access,
            rectification, erasure, restriction, portability, and objection. Our legal bases for
            processing are: (a) contract performance (to deliver the Service); (b) legitimate
            interests (security, product improvement); and (c) consent where required.
          </p>
          <p className={body}>
            If you are a California resident, you have rights under the California Consumer Privacy
            Act (CCPA) including the right to know what we collect, the right to delete, and the
            right to non-discrimination for exercising your rights. We do not sell personal
            information as defined by the CCPA.
          </p>
          <p className={body}>
            To exercise these rights, email{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>
            .
          </p>
        </section>

        <section id="changes" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>10. Changes to This Policy</h2>
          <p className={body}>
            We may update this Privacy Policy from time to time. When we make material changes, we
            will update the "Last Updated" date above and, for active players, notify you by email
            or in-app notice. Continued use of the Service after changes take effect constitutes
            acceptance of the updated policy.
          </p>
        </section>

        <section id="contact" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>11. Contact</h2>
          <ul className={list}>
            <li>
              Privacy:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Security:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Support:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Website:{' '}
              <a href="https://polymyth.quest" className={link}>
                polymyth.quest
              </a>
            </li>
          </ul>
        </section>

        <div className="border-t pt-8 mt-10 text-sm text-muted-foreground">
          <p>
            This Privacy Policy is effective as of May 20, 2026. By using Polymyth on the web or
            through the iOS and Android Mobile Apps, you acknowledge that you have read and
            understood this Privacy Policy.
          </p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
